import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import axios from "axios";
import { ButtonSkeleton } from "./CalendarComponents/SkeletonLoader.jsx";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DatePicker } from "rsuite";
import moment from "moment";
import crossImage from "../../images/singleinvoice/cross-red.svg";
import infoIcon from "../../images/icons/info-icon.svg";
import CalendarIcon from '../ui/CalendarIcon';

const SUPER_ADMIN = 1;
const ADMIN = 2;
const PROJECT_MANAGER = 6;

const ranges = [
    {
        label: "Today",
        value: [new Date(), new Date()],
    },
    {
        label: "Tomorrow",
        value: [moment().add(1, "days").toDate(), moment().add(1, "days").toDate()],
    },
    {
        label: "Next 7 Days",
        value: [new Date(), moment().add(6, "days").toDate()],
    },
];

const GoogleEditEventModal = ({
    editEventModalOpen,
    setEditEventModalOpen,
    isSubmitLoading,
    setIsSubmitLoading,
    selectedGoogleEventForEdit,
    setSelectedGoogleEventForEdit,
    setSelectedEvents,
    setExpandedEventIndex,
    setModalIsOpen,
    eventName,
    setEventName,
    repeatType,
    setRepeatType,
    REPEAT_TYPES,
    eventDate,
    setEventDate,
    eventEndDate,
    setEventEndDate,
    eventStartTime,
    setEventStartTime,
    eventEndTime,
    setEventEndTime,
    eventDescription,
    setEventDescription,
    eventLocation,
    setEventLocation,
    user,
    eventOrganizer,
    setEventOrganizer,
    userProfiles,
    userProfileLoading,
    eventAttendees,
    setEventAttendees,
    externalAttendees,
    setExternalAttendees,
    isVirtual,
    setIsVirtual,
    eventMeetingLink,
    setEventMeetingLink,
    workingLocationType,
    setWorkingLocationType,
    reminders,
    setReminders,
    visibility,
    setVisibility,
    transparency,
    setTransparency,
    iCalUID,
    setICalUID,
    refetchEvents,
    refetchSelectedGoogleEvents,
    token,
}) => {
    const [repeatTypeDropdownOpen, setRepeatTypeDropdownOpen] = useState(false);
    const [organizerDropdownOpen, setOrganizerDropdownOpen] = useState(false);
    const [attendeeInput, setAttendeeInput] = useState('');
    const [attendeeSuggestions, setAttendeeSuggestions] = useState([]);
    const [updateScope, setUpdateScope] = useState('this');
    const [canEditEvent, setCanEditEvent] = useState(true);

    const [eventNameIsValid, setEventNameIsValid] = useState(false);
    const [repeatTypeIsValid, setRepeatTypeIsValid] = useState(false);
    const [eventDateIsValid, setEventDateIsValid] = useState(false);
    const [eventEndDateIsValid, setEventEndDateIsValid] = useState(false);
    const [eventStartTimeIsValid, setEventStartTimeIsValid] = useState(false);
    const [eventEndTimeIsValid, setEventEndTimeIsValid] = useState(false);
    const [workingLocationTypeDropdownOpen, setWorkingLocationTypeDropdownOpen] = useState(false);
    const [visibilityDropdownOpen, setVisibilityDropdownOpen] = useState(false);
    const [transparencyDropdownOpen, setTransparencyDropdownOpen] = useState(false);
    const [reminderMethodDropdownOpen, setReminderMethodDropdownOpen] = useState([]);
    const [reminderMinutesIsValid, setReminderMinutesIsValid] = useState([]);
    
    // Track if the original event was all-day (to preserve event type)
    const [originalIsAllDay, setOriginalIsAllDay] = useState(false);

    // Add REMINDER_METHODS constant
    const REMINDER_METHODS = [
        { value: 'email', label: 'Email' },
        { value: 'popup', label: 'Popup' },
    ];

    const eventNameRef = useRef(null);
    const repeatTypeRef = useRef(null);
    const eventDateRef = useRef(null);
    const eventEndDateRef = useRef(null);
    const eventStartTimeRef = useRef(null);
    const eventEndTimeRef = useRef(null);
    const organizerRef = useRef(null);
    const eventMeetingLinkRef = useRef(null);
    const workingLocationTypeRef = useRef(null);
    const visibilityRef = useRef(null);
    const transparencyRef = useRef(null);
    const reminderMethodRef = useRef([]);

    const { appSettings } = useSelector((state) => state.appSettings);

    // Initialize form with selected event data
    useEffect(() => {
        if (selectedGoogleEventForEdit) {
            const canEdit = selectedGoogleEventForEdit.calendarEmail === selectedGoogleEventForEdit.organizerEmail;
            setCanEditEvent(canEdit);
            setEventName(selectedGoogleEventForEdit.summary || '');
            
            // Detect if original event is all-day (has date but no dateTime)
            const isAllDay = !!selectedGoogleEventForEdit.start?.date && !selectedGoogleEventForEdit.start?.dateTime;
            setOriginalIsAllDay(isAllDay);
            
            // setRepeatType(selectedGoogleEventForEdit.recurrence?.length > 0
            //     ? selectedGoogleEventForEdit.recurrence[0]?.split(';')[0].split('=')[1]?.toLowerCase()
            //     : selectedGoogleEventForEdit.start?.date ? 'all_day' : 'does_not_repeat');
            setEventDate(new Date(selectedGoogleEventForEdit.start?.date || selectedGoogleEventForEdit.start?.dateTime));
            // setEventEndDate(selectedGoogleEventForEdit.recurrence?.length > 0 && selectedGoogleEventForEdit.end?.date
            //     ? new Date(selectedGoogleEventForEdit.end.date)
            //     : selectedGoogleEventForEdit.end?.date && selectedGoogleEventForEdit.end.date !== selectedGoogleEventForEdit.start?.date
            //         ? new Date(selectedGoogleEventForEdit.end.date)
            //         : null);
            // const untilMatch = selectedGoogleEventForEdit.recurrence?.[0]?.match(/UNTIL=(\d{8}T\d{6})Z?/);
            // let untilDate = untilMatch ? untilMatch[1] : null;
            // if (untilDate) {
            //     // Convert from 20250806T235959 → 2025-08-06T23:59:59Z
            //     const formattedDate = untilDate.replace(
            //         /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/,
            //         '$1-$2-$3T$4:$5:$6Z'
            //     );
            //     console.log('formatted UNTIL date', formattedDate);
            //     setEventEndDate(new Date(formattedDate));
            // } else {
            //     setEventEndDate(null);
            // }
            const startDate = selectedGoogleEventForEdit.start?.date?.split('T')[0];
            const endDate = selectedGoogleEventForEdit.end?.date?.split('T')[0];
            const isRecurring = selectedGoogleEventForEdit.recurrence?.length > 0;
            const untilMatch = selectedGoogleEventForEdit.recurrence?.[0]?.match(/UNTIL=(\d{8}T\d{6})Z?/);
            let untilDate = untilMatch ? untilMatch[1] : null;

            if (isRecurring && untilDate) {
                // Convert 20250823T235959 → 2025-08-23T23:59:59Z
                const formattedDate = untilDate.replace(
                    /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/,
                    '$1-$2-$3T$4:$5:$6Z'
                );
                const utcDate = new Date(formattedDate);
                // Subtract 1 day to reflect recurrence UNTIL in local timezone
                const adjustedDate = new Date(utcDate);
                adjustedDate.setDate(adjustedDate.getDate() - 1);
                // Strip time component for pure date use
                const localDate = new Date(adjustedDate.getFullYear(), adjustedDate.getMonth(), adjustedDate.getDate());
                setEventEndDate(localDate);
            } else if (startDate && endDate && startDate !== endDate) {
                setEventEndDate(new Date(endDate));
            } else {
                setEventEndDate(null);
            }
            // For timed events, use dateTime; for all-day events, use date
            // Parse date strings with moment to ensure proper timezone handling
            if (selectedGoogleEventForEdit.start?.dateTime) {
                setEventStartTime(new Date(selectedGoogleEventForEdit.start.dateTime));
            } else if (selectedGoogleEventForEdit.start?.date) {
                // Parse date string with moment to ensure local timezone interpretation
                // Handle both HH:mm and HH:mm:ss formats
                const startDateStr = selectedGoogleEventForEdit.start.date;
                let startMoment;
                if (startDateStr.includes('T')) {
                    // Date string has time component - parse it
                    // Try HH:mm:ss format first, then HH:mm
                    startMoment = moment(startDateStr, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm'], true);
                } else {
                    // Date only - add midnight
                    startMoment = moment(`${startDateStr}T00:00:00`, 'YYYY-MM-DDTHH:mm:ss');
                }
                setEventStartTime(startMoment.toDate());
            } else {
                setEventStartTime('');
            }
            
            if (selectedGoogleEventForEdit.end?.dateTime) {
                setEventEndTime(new Date(selectedGoogleEventForEdit.end.dateTime));
            } else if (selectedGoogleEventForEdit.end?.date) {
                // Parse date string with moment to ensure local timezone interpretation
                // Handle both HH:mm and HH:mm:ss formats
                const endDateStr = selectedGoogleEventForEdit.end.date;
                let endMoment;
                if (endDateStr.includes('T')) {
                    // Date string has time component - parse it
                    // Try HH:mm:ss format first, then HH:mm
                    endMoment = moment(endDateStr, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm'], true);
                } else {
                    // Date only - add midnight
                    endMoment = moment(`${endDateStr}T00:00:00`, 'YYYY-MM-DDTHH:mm:ss');
                }
                setEventEndTime(endMoment.toDate());
            } else {
                setEventEndTime('');
            }
            setEventDescription(selectedGoogleEventForEdit.description || '');
            setEventLocation(selectedGoogleEventForEdit.location?.displayName || '');
            setIsVirtual(!!selectedGoogleEventForEdit.hangoutLink);
            setEventMeetingLink(selectedGoogleEventForEdit.hangoutLink || '');
            setEventOrganizer(
                selectedGoogleEventForEdit.organizer?.email === user.email
                    ? user._id
                    : selectedGoogleEventForEdit.organizer?.email || selectedGoogleEventForEdit.organizer
            );
            setEventAttendees(selectedGoogleEventForEdit.attendees?.filter(a => userProfiles.some(u => u.email === a.email)).map(a => userProfiles.find(u => u.email === a.email)) || []);
            setExternalAttendees(selectedGoogleEventForEdit.attendees?.filter(a => !userProfiles.some(u => u.email === a.email)).map(a => ({
                email: a.email,
                name: a.displayName || null,
                status: a.responseStatus || 'pending',
                reason: null,
                response_date: null,
                additional_notes: null,
            })) || []);
            setWorkingLocationType(selectedGoogleEventForEdit.workingLocationType || null);
            const overrides = Array.isArray(selectedGoogleEventForEdit.reminders?.overrides) ? selectedGoogleEventForEdit.reminders.overrides : [];
            setReminders({
                useDefault: selectedGoogleEventForEdit.reminders?.useDefault ?? true,
                overrides: Array.isArray(selectedGoogleEventForEdit.reminders?.overrides) ? selectedGoogleEventForEdit.reminders.overrides : [],
            });
            setReminderMethodDropdownOpen(new Array(overrides.length).fill(false));
            setVisibility(selectedGoogleEventForEdit.visibility || 'default');
            setTransparency(selectedGoogleEventForEdit.transparency || 'opaque');
            setICalUID(selectedGoogleEventForEdit.iCalUID || null);
            setUpdateScope(selectedGoogleEventForEdit.recurrence?.length > 0 ? 'this' : 'this');
        }
    }, [selectedGoogleEventForEdit, user, userProfiles]);

    // Ensure organizer is included in attendees
    // useEffect(() => {
    //     if (eventOrganizer) {
    //         const organizer = userProfiles.find((u) => u._id === eventOrganizer);
    //         if (organizer && !eventAttendees.some((a) => a._id === eventOrganizer)) {
    //             setEventAttendees([organizer, ...eventAttendees]);
    //         }
    //     }
    // }, [eventOrganizer, userProfiles, eventAttendees, setEventAttendees]);

    // Attendee suggestions
    useEffect(() => {
        if (!attendeeInput) return setAttendeeSuggestions([]);

        const selectedInternalIds = eventAttendees.map(a => a._id);
        const selectedExternalEmails = externalAttendees.map(a => a.email.toLowerCase());

        const filtered = userProfiles.filter((user) =>
            user.email.toLowerCase().includes(attendeeInput.toLowerCase()) &&
            !selectedInternalIds.includes(user._id)
        );

        const exactMatch = userProfiles.find(
            (user) => user.email.toLowerCase() === attendeeInput.toLowerCase()
        );

        if (!exactMatch &&
            attendeeInput.includes('@') &&
            attendeeInput.includes('.') &&
            !selectedExternalEmails.includes(attendeeInput.toLowerCase())) {
            filtered.push({
                _id: null,
                email: attendeeInput,
                name: 'External Email',
                external: true,
            });
        }

        setAttendeeSuggestions(filtered);
    }, [attendeeInput, userProfiles, eventAttendees, externalAttendees]);

    const handleDateSelect = (date) => {
        setEventDate(date);
        setEventDateIsValid(false);
    };

    const handleToggleOrganizerDropdown = () => {
        setOrganizerDropdownOpen(!organizerDropdownOpen);
    };

    const handleSelectAttendee = (attendee) => {
        if (attendee.external) {
            if (!externalAttendees.some((a) => a.email === attendee.email)) {
                setExternalAttendees([...externalAttendees, {
                    email: attendee.email,
                    name: null,
                    status: 'pending',
                    reason: null,
                    response_date: null,
                    additional_notes: null,
                }]);
            }
        } else {
            if (!eventAttendees.some((a) => a._id === attendee._id)) {
                setEventAttendees([...eventAttendees, attendee]);
            }
        }
        setAttendeeInput('');
        setAttendeeSuggestions([]);
    };

    const handleRemoveAttendee = (idOrEmail, isExternal) => {
        if (!isExternal && idOrEmail === eventOrganizer?._id) {
            return;
        }
        if (isExternal) {
            setExternalAttendees(externalAttendees.filter((a) => a.email !== idOrEmail));
        } else {
            setEventAttendees(eventAttendees.filter((a) => a._id !== idOrEmail));
        }
    };

    const handleToggleRepeatTypeDropdown = () => {
        setRepeatTypeDropdownOpen(!repeatTypeDropdownOpen);
    };

    const handleEditEventSubmit = async (e) => {
        e.preventDefault();

        setEventNameIsValid(false);
        setEventDateIsValid(false);
        setEventStartTimeIsValid(false);
        setEventEndTimeIsValid(false);
        setRepeatTypeIsValid(false);
        setEventEndDateIsValid(false);
        setReminderMinutesIsValid(new Array(reminders.overrides.length).fill(false));

        let isValid = true;

        if (!eventName) {
            setEventNameIsValid(true);
            eventNameRef.current?.focus();
            isValid = false;
        }
        if (!eventDate) {
            setEventDateIsValid(true);
            eventDateRef.current?.scrollIntoView({ behavior: "smooth" });
            isValid = false;
        }
        if (!repeatType) {
            setRepeatTypeIsValid(true);
            repeatTypeRef.current?.focus();
            isValid = false;
        }
        if (["daily", "weekly", "monthly"].includes(repeatType) && !eventEndDate) {
            setEventEndDateIsValid(true);
            eventEndDateRef.current?.scrollIntoView({ behavior: "smooth" });
            isValid = false;
        }
        if (
            eventDate && eventEndDate &&
            (["daily", "weekly", "monthly"].includes(repeatType) || moment(eventDate).format('YYYY-MM-DD') !== moment(eventEndDate).format('YYYY-MM-DD')) &&
            moment(eventEndDate).isBefore(moment(eventDate), "day")
        ) {
            toast.error("End date must be on or after the event date!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            setEventEndDateIsValid(true);
            eventEndDateRef.current?.focus();
            isValid = false;
        }
        if (repeatType !== "all_day") {
            if (!eventStartTime) {
                setEventStartTimeIsValid(true);
                eventStartTimeRef.current?.focus();
                isValid = false;
            }
            if (!eventEndTime) {
                setEventEndTimeIsValid(true);
                eventEndTimeRef.current?.focus();
                isValid = false;
            }
            if (eventStartTime && eventEndTime && eventDate && (eventEndDate || eventDate)) {
                const isDifferentDay =
                    !!eventEndDate &&
                    moment(eventDate).format('YYYY-MM-DD') !== moment(eventEndDate).format('YYYY-MM-DD');

                const baseEndDate =
                    (repeatType === 'does_not_repeat' || repeatType === 'all_day')
                        ? (isDifferentDay ? eventEndDate : eventDate)
                        : eventDate;
                const startDateTime = moment(eventDate).set({
                    hour: moment(eventStartTime).hour(),
                    minute: moment(eventStartTime).minute(),
                });
                const endDateTime = moment(baseEndDate).set({
                    hour: moment(eventEndTime).hour(),
                    minute: moment(eventEndTime).minute(),
                });
                if (endDateTime.isSameOrBefore(startDateTime)) {
                    toast.error("End time must be after start time!", {
                        position: "bottom-right",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                    setEventStartTimeIsValid(true);
                    setEventEndTimeIsValid(true);
                    eventEndTimeRef.current?.focus();
                    isValid = false;
                }
            }
        }
        if (repeatType === "all_day" && workingLocationType && eventDate && (eventEndDate || eventDate)) {
            const startDate = moment(eventDate).format('YYYY-MM-DD');
            const endDate = moment(eventDate).format('YYYY-MM-DD');
            if (startDate !== endDate) {
                toast.error("All-day working location events must be exactly one day long!", {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
                setEventEndDateIsValid(true);
                eventEndDateRef.current?.focus();
                isValid = false;
            }
        }
        if (!reminders.useDefault && Array.isArray(reminders.overrides)) {
            const invalidMinutes = reminders.overrides.map((override, index) => {
                if (override.minutes < 0 || override.minutes > 40320) {
                    setReminderMinutesIsValid(prev => {
                        const newState = [...prev];
                        newState[index] = true;
                        return newState;
                    });
                    return true;
                }
                return false;
            });
            if (invalidMinutes.some(Boolean)) {
                toast.error("Reminder minutes must be between 0 and 40320 (4 weeks)!", {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
                isValid = false;
            }
        }
        if (workingLocationType && reminders.useDefault === false && reminders.overrides.length === 0) {
            toast.error("Working location events require at least one reminder or default reminders!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            isValid = false;
        }

        if (!isValid) {
            toast.error("Please fill all required fields!", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            return;
        }

        try {
            setIsSubmitLoading(true);
            const timeZone = global.config.getTimeZone() || 'Asia/Kolkata';
            
            // Build startDateTime - combine eventDate with eventStartTime
            // Use the same format as onChange handlers (space separator) for consistency
            const startDateStr = moment(eventDate).format('YYYY-MM-DD');
            const startHour = eventStartTime ? moment(eventStartTime).hour() : 0;
            const startMinute = eventStartTime ? moment(eventStartTime).minute() : 0;
            const startDateTimeStr = `${startDateStr} ${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}:00`;
            const startDateTime = moment(startDateTimeStr, 'YYYY-MM-DD HH:mm:ss')
                .second(0)
                .millisecond(0);

            // Build endDateTime
            // For recurring events (daily, weekly, monthly, yearly) with times:
            //   - Use eventDate as base for both start and end (single-day event)
            //   - Use eventStartTime and eventEndTime for the times (on the same day)
            //   - Use eventEndDate only for the UNTIL date in recurrence rule
            // For non-recurring events, use eventEndDate if it exists and is different
            const isRecurringWithTimes = ["daily", "weekly", "monthly", "yearly"].includes(repeatType) && (eventStartTime || eventEndTime);
            
            let endDateBase;
            if (repeatType === 'does_not_repeat' || repeatType === 'all_day') {
                // For non-recurring events, check if eventEndDate exists and is different
                const hasDifferentEndDate = eventEndDate && 
                    moment(eventDate).format('YYYY-MM-DD') !== moment(eventEndDate).format('YYYY-MM-DD');
                endDateBase = hasDifferentEndDate ? eventEndDate : eventDate;
            } else if (isRecurringWithTimes) {
                // For recurring events with times, always use eventDate (single-day event)
                endDateBase = eventDate;
            } else {
                // For recurring events without times (all-day), use eventDate as the base
                endDateBase = eventDate;
            }
            
            // Build endDateTime
            let endDateTime;
            if (eventEndTime) {
                if (isRecurringWithTimes) {
                    // For recurring events with times, always use eventDate with eventEndTime time
                    // This ensures it's a single-day event that repeats
                    const endDateBaseStr = moment(eventDate).format('YYYY-MM-DD');
                    const eventEndTimeMoment = moment(eventEndTime);
                    const endHour = eventEndTimeMoment.hour();
                    const endMinute = eventEndTimeMoment.minute();
                    const endDateTimeStr = `${endDateBaseStr} ${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}:00`;
                    endDateTime = moment(endDateTimeStr, 'YYYY-MM-DD HH:mm:ss')
                        .second(0)
                        .millisecond(0);
                } else {
                    // For non-recurring or all-day events, check if eventEndTime is on a different day
                    const eventEndTimeMoment = moment(eventEndTime);
                    const eventEndTimeDate = eventEndTimeMoment.format('YYYY-MM-DD');
                    const eventDateStr = moment(eventDate).format('YYYY-MM-DD');
                    
                    if (eventEndTimeDate !== eventDateStr) {
                        // eventEndTime is on a different day, use eventEndTime directly
                        endDateTime = moment(eventEndTime)
                            .second(0)
                            .millisecond(0);
                    } else {
                        // Same day, combine endDateBase date with eventEndTime time
                        const endDateBaseStr = moment(endDateBase).format('YYYY-MM-DD');
                        const endHour = eventEndTimeMoment.hour();
                        const endMinute = eventEndTimeMoment.minute();
                        const endDateTimeStr = `${endDateBaseStr} ${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}:00`;
                        endDateTime = moment(endDateTimeStr, 'YYYY-MM-DD HH:mm:ss')
                            .second(0)
                            .millisecond(0);
                    }
                }
            } else {
                // No eventEndTime, use endDateBase with default end of day
                endDateTime = moment(endDateBase)
                    .hour(23)
                    .minute(59)
                    .second(0)
                    .millisecond(0);
            }
            
            // Ensure endDateTime is after startDateTime
            if (endDateTime.isSameOrBefore(startDateTime)) {
                console.warn('[GoogleEditEventModal] End time is before or equal to start time, adjusting...', {
                    startDateTime: startDateTime.format('YYYY-MM-DD HH:mm:ss'),
                    endDateTime: endDateTime.format('YYYY-MM-DD HH:mm:ss'),
                });
                // If end is before start, add 1 hour to end
                endDateTime.add(1, 'hour');
            }
            
            console.log('[GoogleEditEventModal] Event edit data:', {
                eventDate: moment(eventDate).format('YYYY-MM-DD HH:mm:ss'),
                eventEndDate: eventEndDate ? moment(eventEndDate).format('YYYY-MM-DD HH:mm:ss') : null,
                eventStartTime: eventStartTime ? moment(eventStartTime).format('YYYY-MM-DD HH:mm:ss') : null,
                eventEndTime: eventEndTime ? moment(eventEndTime).format('YYYY-MM-DD HH:mm:ss') : null,
                repeatType,
                startDateTimeISO: startDateTime.toISOString(),
                endDateTimeISO: endDateTime.toISOString(),
                startDateTimeFormatted: startDateTime.format('YYYY-MM-DD HH:mm:ss'),
                endDateTimeFormatted: endDateTime.format('YYYY-MM-DD HH:mm:ss'),
                startDateTimeValid: startDateTime.isValid(),
                endDateTimeValid: endDateTime.isValid(),
                timeZone: timeZone,
            });

            // For all-day events, Google Calendar requires end date to be the day AFTER start date (exclusive)
            // For timed events, Google Calendar expects dateTime in RFC3339 format
            // When timeZone is specified, dateTime should be in that timezone without 'Z' suffix
            // If user has set times (eventStartTime or eventEndTime), treat as timed event
            // Otherwise, preserve original event type (all-day stays all-day)
            const hasTimesSet = !!(eventStartTime || eventEndTime);
            const shouldBeAllDay = repeatType === "all_day" || (originalIsAllDay && !hasTimesSet);
            
            const startPayload = shouldBeAllDay ? {
                date: moment(eventDate).format('YYYY-MM-DD'),
                timeZone: timeZone,
            } : {
                // Format as RFC3339 in local timezone (without Z suffix) since we're specifying timeZone
                // RFC3339 requires seconds, so use HH:mm:ss format
                dateTime: startDateTime.format('YYYY-MM-DDTHH:mm:ss'),
                timeZone: timeZone,
            };

            const endPayload = shouldBeAllDay ? {
                // For all-day events, end date must be the day after start date (exclusive)
                date: moment(eventDate).add(1, 'day').format('YYYY-MM-DD'),
                timeZone: timeZone,
            } : {
                // Format as RFC3339 in local timezone (without Z suffix) since we're specifying timeZone
                // RFC3339 requires seconds, so use HH:mm:ss format
                dateTime: endDateTime.format('YYYY-MM-DDTHH:mm:ss'),
                timeZone: timeZone,
            };

            const requestData = {
                eventId: selectedGoogleEventForEdit.id,
                summary: eventName,
                start: startPayload,
                end: endPayload,
                description: eventDescription || null,
                location: eventLocation || null,
                attendees: [
                    ...eventAttendees.map(a => ({ email: a.email })),
                    ...externalAttendees.map(a => ({ email: a.email })),
                ],
                isVirtual,
                ...(isVirtual && {
                    conferenceData: {
                        createRequest: {
                            requestId: `meet-${Date.now()}`,
                            conferenceSolutionKey: {
                                type: 'hangoutsMeet',
                            },
                        },
                    },
                }),
                editScope: updateScope === 'this' ? 'thisEvent' : 'thisAndFollowing',
                ...(updateScope === 'this_and_following' && {
                    recurringEventId: selectedGoogleEventForEdit.recurringEventId || selectedGoogleEventForEdit.id.split('_')[0],
                    instanceDate: moment(eventDate).format('YYYY-MM-DD'),
                }),
                ...(repeatType !== 'does_not_repeat' && repeatType !== 'all_day' && {
                    recurrence: [`RRULE:FREQ=${repeatType.toUpperCase()}${eventEndDate ? `;UNTIL=${moment(eventEndDate).format('YYYYMMDD[T235959Z]')}` : ''}`],
                }),
                reminders: {
                    useDefault: reminders.useDefault,
                    overrides: reminders.useDefault ? [] : reminders.overrides,
                },
                visibility,
                transparency,
                googleUserId: selectedGoogleEventForEdit.googleUserId,
                ...(workingLocationType && {
                    workingLocationType,
                    extendedProperties: {
                        private: {
                            workingLocationProperties: JSON.stringify({
                                type: workingLocationType,
                                ...(workingLocationType === 'customLocation' && eventLocation ? { customLocation: { label: eventLocation } } : {}),
                                ...(workingLocationType === 'officeLocation' ? { officeLocation: { officeId: eventLocation || 'default' } } : {}),
                            }),
                        },
                    },
                }),
            };

            await axios.patch(`/api/google-calendar/events/edit/${selectedGoogleEventForEdit.id}`, requestData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            global.config.activityLog(
                window.location.href,
                "Google Event Update",
                `Google Calendar event "${eventName}" updated successfully`
            );
            toast.success("Google Calendar event updated successfully", {
                position: "bottom-right",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });

            refetchSelectedGoogleEvents();
            handleCloseEditModal();
            setModalIsOpen(false);
            setExpandedEventIndex(null);
            setSelectedEvents([]);
        } catch (error) {
            console.error("Error updating Google Calendar event:", error);
            global.config.slackMessage(error.toString());

            if (error.response?.data?.status === "error") {
                toast.error(error.response.data.message, {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            } else {
                toast.error("Something went wrong", {
                    position: "bottom-right",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            }
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const handleCloseEditModal = () => {
        if (!isSubmitLoading) {
            setEditEventModalOpen(false);
            setSelectedGoogleEventForEdit(null);
            setEventStartTime("");
            setEventEndTime("");
            setEventName("");
            setEventDescription("");
            setEventLocation("");
            setEventOrganizer(user._id);
            setEventAttendees([]);
            setExternalAttendees([]);
            setIsVirtual(false);
            setEventMeetingLink("");
            setRepeatType("does_not_repeat");
            setWorkingLocationType(null);
            setReminders({ useDefault: true, overrides: [] });
            setReminderMethodDropdownOpen([]);
            setReminderMinutesIsValid([]);
            setVisibility('default');
            setTransparency('opaque');
            setICalUID(null);
            setEventNameIsValid(false);
            setEventDateIsValid(false);
            setEventStartTimeIsValid(false);
            setEventEndTimeIsValid(false);
            setRepeatTypeIsValid(false);
            setOrganizerDropdownOpen(false);
            setRepeatTypeDropdownOpen(false);
            setUpdateScope('this');
            document.body.classList.remove("ReactModal__Body--open");
        }
    };

    const handleClickOutsideDropdown = (event) => {
        const refs = {
            repeatType: repeatTypeRef.current,
            organizer: organizerRef.current,
            workingLocationType: workingLocationTypeRef.current,
            visibility: visibilityRef.current,
            transparency: transparencyRef.current,
        };

        Object.entries(refs).forEach(([key, ref]) => {
            if (!ref) {
                // Ref is null - validation will handle this
            }
        });

        try {
            if (refs.repeatType && !refs.repeatType.contains(event.target)) {
                setRepeatTypeDropdownOpen(false);
            }
            if (refs.organizer && !refs.organizer.contains(event.target)) {
                setOrganizerDropdownOpen(false);
            }
            if (refs.workingLocationType && !refs.workingLocationType.contains(event.target)) {
                setWorkingLocationTypeDropdownOpen(false);
            }
            if (refs.visibility && !refs.visibility.contains(event.target)) {
                setVisibilityDropdownOpen(false);
            }
            if (refs.transparency && !refs.transparency.contains(event.target)) {
                setTransparencyDropdownOpen(false);
            }
            reminderMethodRef.current.forEach((ref, index) => {
                if (ref && !ref.contains(event.target)) {
                    setReminderMethodDropdownOpen(prev => {
                        const newState = [...prev];
                        newState[index] = false;
                        return newState;
                    });
                }
            });
        } catch (err) {
            console.error('Error in handleClickOutsideDropdown:', err);
        }
    };

    const isRecurring = selectedGoogleEventForEdit?.recurringEventId;

    // Update useEffect for click outside listener
    useEffect(() => {
        if (typeof window === 'undefined' || !document) {
            return;
        }

        document.addEventListener('click', handleClickOutsideDropdown);

        return () => {
            document.removeEventListener('click', handleClickOutsideDropdown);
        };
    }, []);

    return (
        <Modal
            className="client-modal sprint-modal time-off-close calendar-event-create calendar-event-edit"
            isOpen={editEventModalOpen}
            onRequestClose={!isSubmitLoading ? handleCloseEditModal : undefined}
            contentLabel="Edit Google Calendar Event"
            style={{
                content: {
                    height: "50%",
                    width: "30%",
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                },
            }}
        >
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                    <div className="modal timeoff-modal">
                        <div>
                            <div className="modal-client-head">
                                <h2>Edit Google Calendar Event</h2>
                                <div className="modal-close-invite outter-modal-main">
                                    <a onClick={handleCloseEditModal} disabled={isSubmitLoading}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <path d="M1 13L13 1" stroke="black" />
                                            <path d="M1 0.999999L13 13" stroke="black" />
                                        </svg>
                                    </a>
                                </div>
                            </div>
                            <div className="form-field-client-outer">
                                <form onSubmit={handleEditEventSubmit}>
                                    <div className="form-field-client">
                                        <label htmlFor="eventName" className="form-label">
                                            Event Name *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <input
                                                id="eventName"
                                                type="text"
                                                value={eventName}
                                                onChange={(e) => setEventName(e.target.value)}
                                                className={eventNameIsValid ? "input-validation-error form-input" : "form-input"}
                                                ref={eventNameRef}
                                            />
                                            {eventNameIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Event name is required
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-field-client">
                                        <label htmlFor="repeatType" className="form-label">
                                            Repeat Type *
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div ref={repeatTypeRef} className={`cursor ${repeatTypeDropdownOpen ? 'open' : ''}`}>
                                                <span
                                                    id="repeat-type-select"
                                                    onClick={handleToggleRepeatTypeDropdown}
                                                    className={`custom-dropdown-user-new custom-dropdown-select ${repeatTypeIsValid ? 'input-validation-error' : ''}`}
                                                >
                                                    {repeatType ? REPEAT_TYPES.find((rt) => rt.value === repeatType)?.label : 'Select'}
                                                </span>
                                                {repeatTypeDropdownOpen && (
                                                    <div className="custom-dropdown-new">
                                                        <ul className="leave-type-list">
                                                            {REPEAT_TYPES.map((rt) => (
                                                                <li
                                                                    key={rt.value}
                                                                    className={repeatType === rt.value ? 'selected-item' : ''}
                                                                    onClick={() => {
                                                                        setRepeatType(rt.value);
                                                                        setRepeatTypeDropdownOpen(false);
                                                                        if (rt.value === "all_day") {
                                                                            setEventStartTime("");
                                                                            setEventEndTime("");
                                                                        }
                                                                        if (!["daily", "weekly", "monthly"].includes(rt.value)) {
                                                                            setEventEndDate(null);
                                                                        }
                                                                    }}
                                                                >
                                                                    {rt.label}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                            {repeatTypeIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Repeat type is required
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* {["daily", "weekly", "monthly"].includes(repeatType) && ( */}
                                    {isRecurring ? (
                                        <div className="form-field-client scope-sec">
                                            <label className="form-label">Update Scope *</label>
                                            <div className="inner-sec-cal-form">
                                                <div className="radio-group">
                                                    <label className="radio-label">
                                                        <input
                                                            type="radio"
                                                            value="this"
                                                            checked={updateScope === "this"}
                                                            onChange={(e) => setUpdateScope(e.target.value)}
                                                            className="form-radio"
                                                        />
                                                        This Event
                                                    </label>
                                                    <label className="radio-label">
                                                        <input
                                                            type="radio"
                                                            value="this_and_following"
                                                            checked={updateScope === "this_and_following"}
                                                            onChange={(e) => setUpdateScope(e.target.value)}
                                                            className="form-radio"
                                                        />
                                                        This and Following Events
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                    {/* )} */}

                                    <div className="form-field-client">
                                        <label htmlFor="eventDate" className="form-label">
                                            {["daily", "weekly", "monthly"].includes(repeatType) || (eventDate && eventEndDate && moment(eventDate).format('YYYY-MM-DD') !== moment(eventEndDate).format('YYYY-MM-DD'))
                                                ? "Start Date *"
                                                : "Event Date *"}
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div className="date-picker-outer date-picker-outer-all">
                                                <div className="custoom-icon-calender custoom-icon-calender-all">
                                                    <div ref={eventDateRef}>
                                                        <DatePicker
                                                            oneTap
                                                            format="dd/MM/yyyy"
                                                            placeholder="Select Date"
                                                            placement="topEnd"
                                                            ranges={ranges}
                                                            className="project-calender time-off-cal"
                                                            value={eventDate}
                                                            onChange={handleDateSelect}
                                                            isoWeek={appSettings.weekStartDay === 'Monday'}
              caretAs={CalendarIcon}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {eventDateIsValid && (
                                                <span className="text-red-500 text-xs italic">
                                                    Select the event date
                                                </span>
                                            )}
                                        </div>
                                    </div>


                                    {["daily", "weekly", "monthly"].includes(repeatType) || (eventDate && eventEndDate && moment(eventDate).format('YYYY-MM-DD') !== moment(eventEndDate).format('YYYY-MM-DD')) ? (
                                        <div className="form-field-client">
                                            <label htmlFor="eventEndDate" className="form-label">
                                                End Date *
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <div className="date-picker-outer date-picker-outer-all">
                                                    <div className="custoom-icon-calender custoom-icon-calender-all">
                                                        <div ref={eventEndDateRef}>
                                                            <DatePicker
                                                                oneTap
                                                                format="dd/MM/yyyy"
                                                                placeholder="Select End Date"
                                                                placement="topEnd"
                                                                ranges={ranges}
                                                                className="project-calender time-off-cal"
                                                                value={eventEndDate}
                                                                onChange={(date) => setEventEndDate(date)}
                                                                isoWeek={appSettings.weekStartDay === 'Monday'}
                                                                shouldDisableDate={(date) => moment(date).isBefore(moment(eventDate), 'day')}
              caretAs={CalendarIcon}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                {eventEndDateIsValid && (
                                                    <span className="text-red-500 text-xs italic">
                                                        Select the event end date
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ) : null}

                                    {repeatType !== "all_day" && (
                                        <div className="form-field-client radio-sec">
                                            <div className="form-field-client">
                                                <label htmlFor="eventStartTime" className="form-label">
                                                    Start Time *
                                                </label>
                                                <div className="inner-sec-cal-form">
                                                    <input
                                                        id="startTime"
                                                        type="time"
                                                        value={eventStartTime ? moment(eventStartTime).format("HH:mm") : ""}
                                                        onChange={(e) => {
                                                            const [hours, minutes] = e.target.value.split(":").map(Number);
                                                            // Create moment from date string and set time - this ensures local timezone
                                                            const dateStr = moment(eventDate).format('YYYY-MM-DD');
                                                            const updatedStart = moment(`${dateStr} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`, 'YYYY-MM-DD HH:mm:ss').toDate();
                                                            setEventStartTime(updatedStart);
                                                        }}
                                                        className={eventStartTimeIsValid ? "input-validation-error form-input" : "form-input"}
                                                        ref={eventStartTimeRef}
                                                    />
                                                    {eventStartTimeIsValid && (
                                                        <span className="text-red-500 text-xs italic">
                                                            Please select a valid start time
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-field-client">
                                                <label htmlFor="eventEndTime" className="form-label">
                                                    End Time *
                                                </label>
                                                <div className="inner-sec-cal-form">
                                                    <input
                                                        id="endTime"
                                                        type="time"
                                                        value={eventEndTime ? moment(eventEndTime).format("HH:mm") : ""}
                                                        onChange={(e) => {
                                                            const [hours, minutes] = e.target.value.split(":").map(Number);
                                                            // Use eventEndDate if it exists and is different from eventDate, otherwise use eventDate
                                                            // Create moment from date string and set time - this ensures local timezone
                                                            const baseDate = eventEndDate && moment(eventEndDate).format('YYYY-MM-DD') !== moment(eventDate).format('YYYY-MM-DD') 
                                                                ? eventEndDate 
                                                                : eventDate;
                                                            const dateStr = moment(baseDate).format('YYYY-MM-DD');
                                                            const updatedEnd = moment(`${dateStr} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`, 'YYYY-MM-DD HH:mm:ss').toDate();
                                                            setEventEndTime(updatedEnd);
                                                        }}
                                                        className={eventEndTimeIsValid ? "input-validation-error form-input" : "form-input"}
                                                        ref={eventEndTimeRef}
                                                    />
                                                    {eventEndTimeIsValid && (
                                                        <span className="text-red-500 text-xs italic">
                                                            Please select a valid end time
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-field-client">
                                        <label htmlFor="description" className="form-label">
                                            Description
                                        </label>
                                        <textarea
                                            id="description"
                                            name="description"
                                            className="form-textarea"
                                            rows="3"
                                            value={eventDescription}
                                            onChange={(e) => setEventDescription(e.target.value)}
                                        ></textarea>
                                    </div>

                                    {!isVirtual && (
                                        <div className="form-field-client">
                                            <label htmlFor="location" className="form-label">
                                                Location
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <input
                                                    id="location"
                                                    type="text"
                                                    value={eventLocation}
                                                    onChange={(e) => setEventLocation(e.target.value)}
                                                    className="form-input"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* {!isVirtual && (
                                        <div className="form-field-client">
                                            <label htmlFor="workingLocationType" className="form-label">
                                                Working Location Type
                                            </label>
                                            <div className="inner-sec-cal-form">
                                                <div ref={workingLocationTypeRef} className={`cursor ${workingLocationTypeDropdownOpen ? 'open' : ''}`}>
                                                    <span
                                                        id="working-location-type-select"
                                                        onClick={() => setWorkingLocationTypeDropdownOpen(!workingLocationTypeDropdownOpen)}
                                                        className="custom-dropdown-user-new custom-dropdown-select"
                                                    >
                                                        {workingLocationType
                                                            ? { officeLocation: 'Office', home: 'Home', customLocation: 'Custom' }[workingLocationType] || 'None'
                                                            : 'None'}
                                                    </span>
                                                    {workingLocationTypeDropdownOpen && (
                                                        <div className="custom-dropdown-new">
                                                            <ul className="leave-type-list">
                                                                {[
                                                                    { value: '', label: 'None' },
                                                                    { value: 'officeLocation', label: 'Office' },
                                                                    { value: 'home', label: 'Home' },
                                                                    { value: 'customLocation', label: 'Custom' },
                                                                ].map((option) => (
                                                                    <li
                                                                        key={option.value || 'none'}
                                                                        className={workingLocationType === option.value ? 'selected-item' : ''}
                                                                        onClick={() => {
                                                                            setWorkingLocationType(option.value || null);
                                                                            setWorkingLocationTypeDropdownOpen(false);
                                                                        }}
                                                                    >
                                                                        {option.label}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )} */}

                                    <div className="form-field-client">
                                        <label htmlFor="visibility" className="form-label">
                                            Visibility
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div ref={visibilityRef} className={`cursor ${visibilityDropdownOpen ? 'open' : ''}`}>
                                                <span
                                                    id="visibility-select"
                                                    onClick={() => setVisibilityDropdownOpen(!visibilityDropdownOpen)}
                                                    className="custom-dropdown-user-new custom-dropdown-select"
                                                >
                                                    {visibility
                                                        ? { default: 'Default', public: 'Public', private: 'Private' }[visibility]
                                                        : 'Default'}
                                                </span>
                                                {visibilityDropdownOpen && (
                                                    <div className="custom-dropdown-new">
                                                        <ul className="leave-type-list">
                                                            {[
                                                                { value: 'default', label: 'Default' },
                                                                { value: 'public', label: 'Public' },
                                                                { value: 'private', label: 'Private' },
                                                            ].map((option) => (
                                                                <li
                                                                    key={option.value}
                                                                    className={visibility === option.value ? 'selected-item' : ''}
                                                                    onClick={() => {
                                                                        setVisibility(option.value);
                                                                        setVisibilityDropdownOpen(false);
                                                                    }}
                                                                >
                                                                    {option.label}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-field-client">
                                        <label htmlFor="transparency" className="form-label">
                                            Transparency
                                        </label>
                                        <div className="inner-sec-cal-form">
                                            <div ref={transparencyRef} className={`cursor ${transparencyDropdownOpen ? 'open' : ''}`}>
                                                <span
                                                    id="transparency-select"
                                                    onClick={() => setTransparencyDropdownOpen(!transparencyDropdownOpen)}
                                                    className="custom-dropdown-user-new custom-dropdown-select"
                                                >
                                                    {transparency
                                                        ? { opaque: 'Busy', transparent: 'Free' }[transparency]
                                                        : 'Busy'}
                                                </span>
                                                {transparencyDropdownOpen && (
                                                    <div className="custom-dropdown-new">
                                                        <ul className="leave-type-list">
                                                            {[
                                                                { value: 'opaque', label: 'Busy' },
                                                                { value: 'transparent', label: 'Free' },
                                                            ].map((option) => (
                                                                <li
                                                                    key={option.value}
                                                                    className={transparency === option.value ? 'selected-item' : ''}
                                                                    onClick={() => {
                                                                        setTransparency(option.value);
                                                                        setTransparencyDropdownOpen(false);
                                                                    }}
                                                                >
                                                                    {option.label}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-field-client is-virtual-vent">
                                        <label htmlFor="isVirtual" className="form-label">
                                            Virtual Event
                                        </label>
                                        <input
                                            id="isVirtual"
                                            type="checkbox"
                                            checked={isVirtual}
                                            onChange={(e) => setIsVirtual(e.target.checked)}
                                            className="form-checkbox"
                                        />
                                    </div>

                                    <div className="form-field-client guest-input-wrapper">
                                        <label className="form-label">Guests</label>
                                        <div className="suggession-user">
                                            <input
                                                type="text"
                                                value={attendeeInput}
                                                onChange={(e) => canEditEvent && setAttendeeInput(e.target.value)}
                                                placeholder="Add guests"
                                                className="form-input"
                                                disabled={userProfileLoading || !canEditEvent}
                                            />
                                            {userProfileLoading ? (
                                                <ul className="suggestion-dropdown">
                                                    {Array.from({ length: 3 }).map((_, idx) => (
                                                        <li key={idx} style={{ padding: '8px 12px', cursor: 'default' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <div
                                                                    style={{
                                                                        width: '20px',
                                                                        height: '20px',
                                                                        borderRadius: '50%',
                                                                        backgroundColor: '#e5e7eb',
                                                                        animation: 'pulse 1.5s ease-in-out infinite',
                                                                    }}
                                                                />
                                                                <div
                                                                    style={{
                                                                        width: `${150 + idx * 20}px`,
                                                                        height: '18px',
                                                                        backgroundColor: '#e5e7eb',
                                                                        borderRadius: '4px',
                                                                        animation: 'pulse 1.5s ease-in-out infinite',
                                                                    }}
                                                                />
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                attendeeSuggestions.length > 0 && canEditEvent && (
                                                    <ul className="suggestion-dropdown">
                                                        {attendeeSuggestions.map((user, idx) => (
                                                            <li key={user._id || `external-${idx}`} onClick={() => canEditEvent && handleSelectAttendee(user)}>
                                                                <span className="email-icon">
                                                                    <img
                                                                        src={`/Uploads/lowProfileImage/${user.profile_image}`}
                                                                        alt="Email"
                                                                        onError={(e) => {
                                                                            e.target.onerror = null;
                                                                            e.target.src = "/Uploads/lowProfileImage/user_default.png";
                                                                        }}
                                                                    />
                                                                </span>
                                                                <span>{user.email}</span>
                                                                {user.external && <em> (external)</em>}
                                                                {user._id === eventOrganizer && <em> (Organizer)</em>}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )
                                            )}
                                        </div>
                                    </div>

                                    {(eventAttendees.length > 0 || externalAttendees.length > 0) && (
                                        <div className="form-field-client guest-input-wrapper hide-it">
                                            <label className="form-label"></label>
                                            <div className="selected-guests">
                                                {eventAttendees.map((attendee) => (
                                                    <div key={attendee._id} className="guest-chip">
                                                        <span className={`email-icon ${attendee._id === eventOrganizer ? 'organizer-icon' : ''}`}>
                                                            <img
                                                                src={`/Uploads/lowProfileImage/${attendee.profile_image}`}
                                                                alt="Email"
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = "/Uploads/lowProfileImage/user_default.png";
                                                                }}
                                                            />
                                                        </span>
                                                        <span className="guest-chip-name-sec">
                                                            <span className={`attendee-name ${attendee._id === eventOrganizer ? 'organizer-name' : ''}`}>
                                                                {attendee.first_name ? `${attendee.first_name} ${attendee.last_name || ''}` : attendee.company_name || attendee.email}
                                                                {attendee._id === eventOrganizer && <em> (Organizer)</em>}
                                                            </span>
                                                            {attendee._id !== eventOrganizer && canEditEvent && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveAttendee(attendee._id, false)}
                                                                    className=""
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                        </span>
                                                    </div>
                                                ))}
                                                {externalAttendees.map((attendee, i) => (
                                                    <div key={`external-${i}`} className="guest-chip">
                                                        <span className="email-icon">
                                                            <img
                                                                src={`/Uploads/lowProfileImage/user_default.png`}
                                                                alt="Email"
                                                            />
                                                        </span>
                                                        <span className="guest-chip-name-sec">
                                                            <span>{attendee.email} (external)</span>
                                                            {canEditEvent && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveAttendee(attendee.email, true)}
                                                                    className=""
                                                                >
                                                                    ×
                                                                </button>
                                                            )}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-field-client-outer-reminder">
                                        <label className="form-label reminder-heading" htmlFor="reminder-settings">
                                            Reminder Settings
                                            <span className="reminder-info" title="Choose how and when to be reminded about this event.">
                                                <img src={infoIcon} alt="Info Icon" />
                                            </span>
                                        </label>
                                        <div className="form-field-client is-virtual-vent">
                                            <label className="form-checkbox-label">
                                                <input
                                                    type="checkbox"
                                                    checked={reminders.useDefault}
                                                    onChange={(e) => {
                                                        setReminders({
                                                            ...reminders,
                                                            useDefault: e.target.checked,
                                                            overrides: e.target.checked ? [] : Array.isArray(reminders.overrides) ? reminders.overrides : [],
                                                        });
                                                        setReminderMethodDropdownOpen([]);
                                                        setReminderMinutesIsValid([]);
                                                    }}
                                                    className="form-checkbox"
                                                />
                                                Use Default Reminders
                                            </label>
                                        </div>
                                        {!reminders.useDefault && Array.isArray(reminders.overrides) && (
                                            <div className="reminders-overrides">
                                                {reminders.overrides.map((override, index) => (
                                                    <div key={index} className="reminder-override">
                                                        <div className="reminder-overrid-inner">
                                                            <div className="form-field-client">
                                                                <label htmlFor={`reminder-method-${index}`} className="form-label">
                                                                    Reminder Method
                                                                </label>
                                                                <div
                                                                    ref={(el) => (reminderMethodRef.current[index] = el)}
                                                                    className={`cursor ${reminderMethodDropdownOpen[index] ? 'open' : ''}`}
                                                                >
                                                                    <span
                                                                        id={`reminder-method-${index}`}
                                                                        onClick={() => {
                                                                            setReminderMethodDropdownOpen(prev => {
                                                                                const newState = [...prev];
                                                                                newState[index] = !newState[index];
                                                                                return newState;
                                                                            });
                                                                        }}
                                                                        className="custom-dropdown-user-new custom-dropdown-select"
                                                                        role="button"
                                                                        tabIndex={0}
                                                                        aria-haspopup="listbox"
                                                                        aria-expanded={reminderMethodDropdownOpen[index]}
                                                                        aria-describedby={`reminder-method-desc-${index}`}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                                setReminderMethodDropdownOpen(prev => {
                                                                                    const newState = [...prev];
                                                                                    newState[index] = !newState[index];
                                                                                    return newState;
                                                                                });
                                                                            }
                                                                        }}
                                                                    >
                                                                        {REMINDER_METHODS.find((method) => method.value === override.method)?.label || 'Select Method'}
                                                                    </span>
                                                                    {reminderMethodDropdownOpen[index] && (
                                                                        <div className="custom-dropdown-new">
                                                                            <ul className="client-list" role="listbox">
                                                                                {REMINDER_METHODS.map((method) => (
                                                                                    <li
                                                                                        key={method.value}
                                                                                        className={override.method === method.value ? 'selected-item' : ''}
                                                                                        onClick={() => {
                                                                                            const newOverrides = [...reminders.overrides];
                                                                                            newOverrides[index].method = method.value;
                                                                                            setReminders({ ...reminders, overrides: newOverrides });
                                                                                            setReminderMethodDropdownOpen(prev => {
                                                                                                const newState = [...prev];
                                                                                                newState[index] = false;
                                                                                                return newState;
                                                                                            });
                                                                                        }}
                                                                                        role="option"
                                                                                        aria-selected={override.method === method.value}
                                                                                        tabIndex={0}
                                                                                        onKeyDown={(e) => {
                                                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                                                const newOverrides = [...reminders.overrides];
                                                                                                newOverrides[index].method = method.value;
                                                                                                setReminders({ ...reminders, overrides: newOverrides });
                                                                                                setReminderMethodDropdownOpen(prev => {
                                                                                                    const newState = [...prev];
                                                                                                    newState[index] = false;
                                                                                                    return newState;
                                                                                                });
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        {method.label}
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                    )}
                                                                    <p id={`reminder-method-desc-${index}`} className="tooltip-calender-event">
                                                                        {override.method === 'popup' ? 'Shows an in-app alert.' : 'Sends an email to attendees.'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="form-field-client">
                                                                <label htmlFor={`reminder-time-${index}`} className="sr-only">
                                                                    Reminder Time (minutes before)
                                                                </label>
                                                                <div className="form-field-inner-sec inner-sec-cal-form">
                                                                    <input
                                                                        type="number"
                                                                        value={override.minutes}
                                                                        onChange={(e) => {
                                                                            const newOverrides = [...reminders.overrides];
                                                                            newOverrides[index].minutes = Math.max(0, parseInt(e.target.value) || 0);
                                                                            setReminders({ ...reminders, overrides: newOverrides });
                                                                            setReminderMinutesIsValid(prev => {
                                                                                const newState = [...prev];
                                                                                newState[index] = (parseInt(e.target.value) || 0) < 0;
                                                                                return newState;
                                                                            });
                                                                        }}
                                                                        min="0"
                                                                        placeholder="Minutes before"
                                                                        className="form-input w-1/2"
                                                                        id={`reminder-time-${index}`}
                                                                        aria-describedby={`reminder-time-desc-${index}`}
                                                                    />
                                                                    <p id={`reminder-time-desc-${index}`} className="tooltip-calender-event">
                                                                        Select when to receive the reminder.
                                                                    </p>
                                                                </div>
                                                                {reminderMinutesIsValid[index] && (
                                                                    <span id={`custom-reminder-error-${index}`} className="text-red-500 text-xs italic">
                                                                        Please enter a valid number of minutes (0 or greater)
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const newOverrides = reminders.overrides.filter((_, i) => i !== index);
                                                                setReminders({ ...reminders, overrides: newOverrides });
                                                                setReminderMethodDropdownOpen(prev => prev.filter((_, i) => i !== index));
                                                                setReminderMinutesIsValid(prev => prev.filter((_, i) => i !== index));
                                                            }}
                                                            className="remove-override"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                                <path d="M1 13L13 1" stroke="black" />
                                                                <path d="M1 0.999999L13 13" stroke="black" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setReminders({
                                                            ...reminders,
                                                            overrides: [...(Array.isArray(reminders.overrides) ? reminders.overrides : []), { method: 'email', minutes: 10 }],
                                                        });
                                                        setReminderMethodDropdownOpen(prev => [...prev, false]);
                                                        setReminderMinutesIsValid(prev => [...prev, false]);
                                                    }}
                                                    className="add-override"
                                                >
                                                    <span className="icon-invoice">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                                            <path d="M6.5 0V13" stroke="#6A7988" strokeWidth="1.5" />
                                                            <path d="M13 6.5L-2.98023e-07 6.5" stroke="#6A7988" strokeWidth="1.5" />
                                                        </svg>
                                                    </span>
                                                    Add Reminder
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <button type="submit" disabled={isSubmitLoading} className="submit-client submit-client-cal">
                                        {isSubmitLoading ? (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                <ButtonSkeleton width="130px" height="18px" />
                                            </div>
                                        ) : (
                                            "Update Google Event"
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default GoogleEditEventModal;